import { Tag } from 'antd';
import { ColumnsType } from 'antd/es/table/Table';
import { format } from 'date-fns';
import { DataAuditUnitRow } from 'gql/types';
import { formatCsvDate } from 'modules/dashboard/utils/csvDate';
import { Link } from 'react-router-dom';

import { LINK_COLOR, TYPE_COLORS, UNIT_CATEGORY_IDS } from '../../constants';
import { getUniqueValues } from '../../utils/filterUtils';

function uniqueFilters(
  rows: DataAuditUnitRow[],
  getValue: (r: DataAuditUnitRow) => string | null | undefined
): { text: string; value: string }[] {
  return getUniqueValues(rows, getValue).map((v) => ({ text: v, value: v }));
}

export function getUnitTableColumns(
  unitRows: DataAuditUnitRow[],
  unitCategoryId: number,
  ffpDblProductIds?: number[]
): ColumnsType<DataAuditUnitRow> {
  const dblIds = ffpDblProductIds ?? [];

  return [
    {
      title: 'Product',
      dataIndex: 'productShortId',
      key: 'productShortId',
      width: 80,
      render: (shortId: number) => (
        <Link
          to={`/products/${shortId}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: LINK_COLOR }}
        >
          {shortId}
        </Link>
      ),
      sorter: (a, b) => (a.productShortId ?? 0) - (b.productShortId ?? 0),
    },
    {
      title: 'Unit Type',
      dataIndex: 'unitType',
      key: 'unitType',
      width: 70,
      filters: uniqueFilters(unitRows, (r) => r.unitType),
      onFilter: (v, r) => r.unitType === v,
    },
    {
      title: 'Status',
      dataIndex: 'productStatus',
      key: 'productStatus',
      width: 80,
      filters: uniqueFilters(unitRows, (r) => r.productStatus),
      onFilter: (v, r) => r.productStatus === v,
    },
    {
      title: 'Created',
      dataIndex: 'productCreatedAt',
      key: 'productCreatedAt',
      width: 110,
      render: (v: string) => format(new Date(v), 'MM/dd/yyyy'),
      sorter: (a, b) =>
        new Date(a.productCreatedAt).getTime() -
        new Date(b.productCreatedAt).getTime(),
      defaultSortOrder: 'descend' as const,
    },
    {
      title: 'Donor',
      dataIndex: 'donorName',
      key: 'donorName',
      width: 120,
      render: (name: string, record) => (
        <Link
          to={`/donors/${record.donorShortId}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: LINK_COLOR }}
        >
          {name}
        </Link>
      ),
      filters: uniqueFilters(unitRows, (r) => r.donorName),
      onFilter: (v, r) => r.donorName === v,
      filterSearch: true,
    },
    {
      title: 'Owner',
      dataIndex: 'ownerFullName',
      key: 'ownerFullName',
      width: 120,
      filters: uniqueFilters(unitRows, (r) => r.ownerFullName),
      onFilter: (v, r) => r.ownerFullName === v,
      filterSearch: true,
    },
    {
      title: 'Location',
      dataIndex: 'donorLocation',
      key: 'donorLocation',
      width: 100,
    },
    {
      title: 'Region',
      dataIndex: 'regionName',
      key: 'regionName',
      width: 100,
      filters: uniqueFilters(unitRows, (r) => r.regionName),
      onFilter: (v, r) => r.regionName === v,
    },
    {
      title: 'Donor Status',
      dataIndex: 'donorStatus',
      key: 'donorStatus',
      width: 90,
      filters: uniqueFilters(unitRows, (r) => r.donorStatus),
      onFilter: (v, r) => r.donorStatus === v,
    },
    ...(unitCategoryId === UNIT_CATEGORY_IDS.FFP
      ? [
          {
            title: 'SGL/DBL',
            key: 'ffpSglDbl',
            width: 80,
            align: 'center' as const,
            render: (_: unknown, record: DataAuditUnitRow) => {
              const label = dblIds.includes(record.woocommerceProductId)
                ? 'DBL'
                : 'SGL';
              return (
                <Tag
                  color={label === 'DBL' ? TYPE_COLORS.DBL : TYPE_COLORS.SGL}
                >
                  {label}
                </Tag>
              );
            },
            filters: [
              { text: 'SGL', value: 'SGL' },
              { text: 'DBL', value: 'DBL' },
            ],
            onFilter: (v: unknown, r: DataAuditUnitRow) => {
              const label = dblIds.includes(r.woocommerceProductId)
                ? 'DBL'
                : 'SGL';
              return label === v;
            },
          },
        ]
      : []),
  ];
}

export const UNIT_CSV_EXTRACTORS: Record<
  string,
  (r: DataAuditUnitRow) => string
> = {
  productShortId: (r) => String(r.productShortId ?? ''),
  unitType: (r) => r.unitType || '',
  productStatus: (r) => r.productStatus || '',
  productCreatedAt: (r) =>
    r.productCreatedAt ? formatCsvDate(r.productCreatedAt) : '',
  donorName: (r) => r.donorName || '',
  ownerFullName: (r) => r.ownerFullName || '',
  donorLocation: (r) => r.donorLocation || '',
  regionName: (r) => r.regionName || '',
  donorStatus: (r) => r.donorStatus || '',
};
